/**
 * Partner Company Profile Tab — Edit company info + upload documents
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Building2, Save, Loader2, Upload, CheckCircle, Clock, FileText } from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const DOC_LABELS = { kbis: 'Kbis', rc_pro: 'RC Professionnelle' };

export default function PartnerCompanyTab({ onRefresh }) {
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          axios.get(`${API}/partner/profile`, { headers: getHeaders() }),
          axios.get(`${API}/partner/documents`, { headers: getHeaders() }),
        ]);
        setProfile(pRes.data);
        setForm({
          name: pRes.data.name || '',
          company_name: pRes.data.company_name || '',
          siret: pRes.data.siret || '',
          address: pRes.data.address || '',
          city: pRes.data.city || '',
          postal_code: pRes.data.postal_code || '',
          phone: pRes.data.phone || '',
          email: pRes.data.email || '',
        });
        setDocs(dRes.data?.documents || []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/partner/profile`, form, { headers: getHeaders() });
      alert('Profil mis à jour');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('document_type', docType);
      formData.append('file', file);
      await axios.post(`${API}/partner/documents/upload`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const dRes = await axios.get(`${API}/partner/documents`, { headers: getHeaders() });
      setDocs(dRes.data?.documents || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur upload');
    }
    setUploading(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="company-tab">
      {/* Company Info */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-[#C9A24D]" /> Informations entreprise
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'company_name', label: 'Raison sociale' },
              { key: 'name', label: 'Nom commercial' },
              { key: 'siret', label: 'SIRET' },
              { key: 'email', label: 'Email' },
              { key: 'phone', label: 'Téléphone' },
              { key: 'address', label: 'Adresse' },
              { key: 'city', label: 'Ville' },
              { key: 'postal_code', label: 'Code postal' },
            ].map(f => (
              <div key={f.key}>
                <Label className="text-white/60 text-xs mb-1 block">{f.label}</Label>
                <Input
                  value={form[f.key] || ''}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  className="bg-[#0B0B0B] border-[#C9A24D]/20 text-white"
                  data-testid={`company-${f.key}`}
                />
              </div>
            ))}
          </div>
          <Button onClick={handleSave} disabled={saving} className="mt-4 bg-[#C9A24D] hover:bg-[#b8913f] text-black" data-testid="save-company-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#C9A24D]" /> Documents entreprise
          </h3>
          <div className="space-y-3">
            {docs.map(d => (
              <div key={d.type} className="flex items-center justify-between p-3 bg-[#0B0B0B] rounded-lg" data-testid={`doc-${d.type}`}>
                <div className="flex items-center gap-3">
                  {d.uploaded ? (
                    d.status === 'PENDING' ? <Clock className="w-5 h-5 text-amber-400" /> : <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <FileText className="w-5 h-5 text-white/30" />
                  )}
                  <div>
                    <p className="text-white text-sm font-medium">{d.label}</p>
                    {d.uploaded && <p className="text-white/40 text-xs">{d.file_name} · {d.status === 'PENDING' ? 'En attente' : 'Approuvé'}</p>}
                  </div>
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => handleUpload(d.type, e.target.files[0])} />
                  <span className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition ${
                    uploading === d.type ? 'bg-[#C9A24D]/20 text-[#C9A24D]' : 'bg-[#C9A24D]/10 text-[#C9A24D] hover:bg-[#C9A24D]/20'
                  }`}>
                    {uploading === d.type ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    {d.uploaded ? 'Remplacer' : 'Uploader'}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
